import { useCallback, useRef } from 'react';

// The idempotency key that the breakdown surface takes as `request_id`.
//
// Creating a tier, proposing a batch, agreeing a tier and authorizing a
// selection all accept one. The backend writes a receipt — actor, project,
// operation, digest of the input and the full result — in the SAME transaction
// as the mutation, and then:
//
//   same key + same input  → the original result, with `request.replayed: true`
//   same key + other input → 409, `gate: idempotencia`
//
// WHICH MAKES THE KEY'S LIFETIME THE WHOLE POINT. It has to survive a retry and
// die on an edit:
//
//   - A lost answer (no response, a 5xx, a cut connection) is the case the
//     receipt exists for. Resending the same body under the same key is the
//     only correct move. Minting a fresh one there authorizes the selection
//     TWICE — the exact duplication the mechanism was built to prevent.
//   - A changed body under the old key is a different operation wearing the
//     same name, and the backend refuses it. So content decides the key.
//
// NOT TO BE CONFUSED WITH `X-Request-ID`, which api.js already puts on every
// request. That one is per HTTP call, fresh each time, and exists for tracing:
// two retries of one operation SHOULD carry two different trace ids and the
// same `request_id`. Reusing the trace header here would break both jobs at
// once.

// Stable across key order, so `{a, b}` and `{b, a}` are one operation and do
// not mint two keys for the same intent. Arrays keep their order, because in
// a batch the order is content.
export function signatureOf(value) {
    if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'undefined';
    if (Array.isArray(value)) return `[${value.map(signatureOf).join(',')}]`;
    const keys = Object.keys(value).sort();
    return `{${keys.map(key => `${JSON.stringify(key)}:${signatureOf(value[key])}`).join(',')}}`;
}

export function newRequestId() {
    return crypto.randomUUID();
}

// Returns `keyFor(payload)`: the same key while the payload is the same, a new
// one the moment it changes. Call it at send time, with the body about to go
// out, and call it again with the SAME body to retry.
export function useRequestId() {
    const held = useRef({ signature: null, key: null });

    return useCallback((payload) => {
        const signature = signatureOf(payload);
        if (held.current.signature !== signature) {
            held.current = { signature, key: newRequestId() };
        }
        return held.current.key;
    }, []);
}
